import PropTypes from "prop-types"
import { useState, useEffect, useMemo } from "react"
import { isEmpty, map } from "lodash"
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material"

const AIResultsTable = ({ records = [], loading = false, question = "" }) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  // Reset pagination when data changes
  useEffect(() => {
    setPage(0)
  }, [records])

  // Get column headers from the first row of data
  const columns = useMemo(() => {
    if (isEmpty(records)) return []
    return Object.keys(records[0]).filter(col => col.toLowerCase() !== "id")
  }, [records])

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // If no question then there can't be results
  if (!question) { return null }

  // If there's no data at all, don't render anything
  if (isEmpty(records)) {
    // Don't return null - instead return empty content
    return (
      <Box className="ai-quick-answers-results">
        {loading ? (
          <Box className="loading-container">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Typography variant="body1" className="no-results-message">
            No results to display.
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box className="ai-quick-answers-results">
      <Typography variant="h6" className="question-header">
        Results for: <Typography component="span" className="question-text">{question}</Typography>
      </Typography>

      <TableContainer component={Paper} className="results-table-container">
        <Table size="small" aria-label="results table">
          <TableHead>
            <TableRow className="results-table-head">
              {map(columns, (column) => (
                <TableCell
                  key={column}
                  padding="checkbox"
                  align="left"
                  className="cell-header"
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {map(records, (row, index) => (
              <TableRow key={row.id} className="row-hover">
                {map(columns, (column) => (
                  <TableCell
                    key={`${index}-${column}`}
                    padding="checkbox"
                    align="left"
                    className="cell-data"
                  >
                    {typeof row[column] === "number"
                      // TODO: Need to revisit formatting requirements
                        ? Number(row[column]).toFixed(4)
                        : row[column]}
                  </TableCell>
                  ))}
              </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 25]}
          component="div"
          count={records.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  )
}

AIResultsTable.propTypes = {
  records: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  question: PropTypes.string,
}

export default AIResultsTable
